/**
 * In-car dial deep-link bridge.
 *
 * Listens for `elefa://dial?to=<e164>` (and its universal-link mirror
 * `https://elefa.tel/dial?to=...`) and routes the user into the dialer
 * with the number pre-filled and auto-call armed. Triggered by:
 *
 *   - Android Auto: ElefaCarAppService → Intent(Intent.ACTION_VIEW, elefa://dial?to=…)
 *   - iOS CarPlay:  ElefaCarPlaySceneDelegate.placeCall → UIApplication.open
 *   - Future: shortcuts, Siri intents, share-sheet redials, etc.
 *
 * Mirrors the proven pattern in useCheckoutDeepLink — we never invoke
 * ElefaSip directly here. The dialer page owns the place-call flow,
 * permissions, network checks, and rate display, so funnelling through
 * it keeps a single audited entry point for outbound calls.
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { App, type URLOpenListenerEvent } from "@capacitor/app";

const DIAL_SCHEME = "elefa";
const DIAL_HOST = "dial";
const DIAL_WEB_HOSTS = new Set(["elefa.tel", "www.elefa.tel"]);

/** Strip everything except `+` and digits — defence in depth against
 *  hostile payloads handed to us by the OS / car host. */
function sanitiseE164(raw: string | null): string {
  if (!raw) return "";
  const cleaned = raw.replace(/[^\d+]/g, "");
  // Must start with + and have 6-15 digits per E.164.
  if (!/^\+\d{6,15}$/.test(cleaned)) return "";
  return cleaned;
}

export default function useDialDeepLink() {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let handle: Awaited<ReturnType<typeof App.addListener>> | null = null;

    const routeFromUrl = (rawUrl?: string | null) => {
      if (!rawUrl) return;
      try {
        const url = new URL(rawUrl);
        const isCustomScheme =
          url.protocol.replace(":", "") === DIAL_SCHEME &&
          url.hostname === DIAL_HOST;
        const isUniversalLink =
          (url.protocol === "https:" || url.protocol === "http:") &&
          DIAL_WEB_HOSTS.has(url.hostname) &&
          url.pathname === `/${DIAL_HOST}`;
        if (!isCustomScheme && !isUniversalLink) return;

        const to = sanitiseE164(url.searchParams.get("to"));
        if (!to) {
          // Fall back to an empty dialer so the user can still place a
          // call; never silently swallow the deep link.
          navigateRef.current("/connect-portal/dial", { replace: true });
          return;
        }

        const qs = new URLSearchParams({ number: to, autoCall: "1", tab: "keypad" });
        navigateRef.current(`/connect-portal/dial?${qs.toString()}`, { replace: true });
      } catch {
        /* malformed URL — ignore */
      }
    };

    const handler = (event: URLOpenListenerEvent) => routeFromUrl(event.url);

    App.addListener("appUrlOpen", handler).then((h) => {
      handle = h;
    });
    // Cold-launch from the car host. getLaunchUrl() resolves to `null` on a
    // normal launch (no deep link) — guard against destructure-of-null crash.
    App.getLaunchUrl()
      .then((res) => {
        const url = res?.url;
        if (url) routeFromUrl(url);
      })
      .catch(() => {});

    return () => {
      handle?.remove();
    };
  }, []);
}
